import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Kanban, Users, FolderKanban, Tag, Settings, LogOut, Menu, X, Activity, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';

function DashboardLayout({ children, token, onLogout }) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', path: '/', icon: LayoutDashboard, testId: 'nav-dashboard' },
    { name: 'Kanban Board', path: '/kanban', icon: Kanban, testId: 'nav-kanban' },
    { name: 'Utenti', path: '/users', icon: Users, testId: 'nav-users' },
    { name: 'Progetti', path: '/projects', icon: FolderKanban, testId: 'nav-projects' },
    { name: 'Categorie', path: '/categories', icon: Tag, testId: 'nav-categories' },
    { name: 'Calendario', path: '/calendar-dashboard', icon: CalendarDays, testId: 'nav-calendar' },
    { name: 'Activity Log', path: '/activity-logs', icon: Activity, testId: 'nav-activity-logs' },
    { name: 'Impostazioni', path: '/settings', icon: Settings, testId: 'nav-settings' },
  ];

  return (
    <div className="min-h-screen bg-[rgb(9,9,11)] flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:sticky top-0 left-0 h-screen w-64 bg-[rgb(24,24,27)]/50 backdrop-blur-sm
          border-r border-[rgb(39,39,42)]/40 flex flex-col z-50
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="p-6 border-b border-[rgb(39,39,42)]/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="/assets/logo_gmed.svg" 
                alt="Gmed Logo" 
                className="h-8"
                data-testid="sidebar-logo"
              />
              <h1 className="text-xl font-bold font-mono text-gradient" data-testid="sidebar-title">
                MantisBT BI
              </h1>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
              data-testid="close-sidebar-button"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <p className="text-[rgb(161,161,170)] text-xs font-mono uppercase tracking-widest mt-2">
            Control Room
          </p>
        </div>

        <nav className="flex-1 p-4 space-y-2" data-testid="sidebar-navigation">
          {navigation.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={item.testId}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-md font-mono text-sm
                  border border-transparent
                  hover:bg-white/5 hover:border-[rgb(59,130,246)]/50
                  transition-all duration-200 ease-out
                  ${
                    isActive
                      ? 'bg-[rgb(59,130,246)]/10 border-[rgb(59,130,246)]/50 text-[rgb(59,130,246)]'
                      : 'text-[rgb(161,161,170)] hover:text-white'
                  }
                `}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-[rgb(39,39,42)]/50">
          <Button
            onClick={onLogout}
            data-testid="logout-button"
            variant="ghost"
            className="w-full justify-start text-[rgb(239,68,68)] hover:text-[rgb(239,68,68)] hover:bg-[rgb(239,68,68)]/10 font-mono"
          >
            <LogOut className="h-5 w-5 mr-3" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="lg:hidden p-4 border-b border-[rgb(39,39,42)]/40 bg-[rgb(24,24,27)]/50 backdrop-blur-sm sticky top-0 z-30">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            data-testid="open-sidebar-button"
          >
            <Menu className="h-6 w-6" />
          </Button>
        </div>
        <div className="p-6 md:p-8" data-testid="dashboard-content">
          {children}
        </div>
      </main>
    </div>
  );
}

export default DashboardLayout;